import { useEffect, useState } from "react";
import { Send, Loader2, X, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Send-Invoice modal. Opens when the admin hits "Send Invoice" on any order row.
 *
 * Lets the admin:
 *   • add a personal note that's rendered above the line-items table in the
 *     branded receipt template ("A note from Fresh Fast Foods")
 *   • add ad-hoc CC recipients on top of the always-present internal CC
 *
 * The backend endpoint accepts the same body shape for cash / PO / stripe
 * order types — the correct receipt template is selected server-side.
 */
export default function SendInvoiceModal({ order, onClose, onSent }) {
  const [message, setMessage] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setMessage("");
    setCcInput("");
    setError("");
  }, [order?.id]);

  if (!order) return null;
  const total = Number(order.total || 0);
  const customerEmail = order.customer_email || "";
  const customerLabel = order.customer_name || customerEmail || order.id;

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    setSending(true);
    setError("");
    try {
      // Split cc on commas, spaces, semicolons — mirror how humans paste addresses.
      const extra_cc = ccInput
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes("@"));
      await api.post(`/admin/orders/${order.id}/send-invoice`, {
        message: message.trim() || null,
        extra_cc: extra_cc.length > 0 ? extra_cc : null,
      });
      onSent?.({ note_included: !!message.trim(), extra_cc });
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Send failed"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="send-invoice-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/5">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-teal/15 text-brand-teal shrink-0">
              <Mail className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="font-display font-black text-[18px] leading-tight text-brand-charcoal truncate">
                Send Invoice
              </p>
              <p className="text-[12px] text-brand-charcoal/55 truncate">
                {customerLabel} · ${total.toFixed(2)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-brand-charcoal/60 hover:bg-brand-charcoal/5 hover:text-brand-charcoal transition-colors"
            data-testid="send-invoice-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSend} className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {/* Recipient (read-only) */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-1.5">
              To
            </p>
            <div className="rounded-xl bg-brand-cream/40 border border-black/10 px-3 py-2.5 text-[13px] text-brand-charcoal">
              {customerEmail || <span className="italic text-brand-charcoal/50">no email on file</span>}
            </div>
          </div>

          {/* CC */}
          <div>
            <label
              htmlFor="send-invoice-cc"
              className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 block mb-1.5"
            >
              CC (optional)
            </label>
            <input
              id="send-invoice-cc"
              type="text"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              data-testid="send-invoice-cc"
              placeholder="accountant@nycha.gov, ops@…"
              className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-[14px] text-brand-charcoal focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal"
            />
            <p className="mt-1 text-[10.5px] text-brand-charcoal/50">
              Separate multiple with commas. Our internal CC is always attached automatically.
            </p>
          </div>

          {/* Personal message */}
          <div>
            <label
              htmlFor="send-invoice-message"
              className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 block mb-1.5"
            >
              Message to customer (optional)
            </label>
            <textarea
              id="send-invoice-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="send-invoice-message"
              rows={5}
              maxLength={2000}
              placeholder="e.g. Hi Bridgitte — attached is your invoice for the July 20th event. Let me know if you'd like to change any of the sliders. — Chef"
              className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-[14px] text-brand-charcoal focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal resize-none"
            />
            <p className="mt-1 text-[10.5px] text-brand-charcoal/50">
              Shown as a highlighted &quot;A note from Fresh Fast Foods&quot; block above the line items. {message.length}/2000
            </p>
          </div>

          {error && (
            <div
              data-testid="send-invoice-error"
              className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-[13px]"
            >
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-black/5 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            data-testid="send-invoice-cancel"
            className="rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] hover:border-brand-charcoal/40 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !customerEmail}
            data-testid="send-invoice-submit"
            className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending…" : "Send Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}
